import Avo from 'analytics/Avo';
import If from 'components/atoms/If';
import type { CarouselItem } from 'components/molecules/Carousel';
import WebView from 'components/molecules/WebView';
import { useConfig } from 'context/app-config';
import { useDiscoveryCardPressHandler } from 'packages/careem/hooks/useDiscoveryCardPressHandler';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import type { ServiceContentType } from '../ServiceTypeOfferSheet';
import FAQCard from './faq-card';
import { StyledContainer, StyledRow, StyledTitle } from './HomeCarousel.styles';

interface HomeFAQCarouselProps {
  items?: CarouselItem[];
  openPricingSheet?: () => void;
  toggleOnboardingSheet?: () => void;
  onOpenServiceOfferSheet?: (type: ServiceContentType) => void;
  className?: string;
}

const FAQHomeCarousel = ({ items = [], openPricingSheet, onOpenServiceOfferSheet, toggleOnboardingSheet, className }: HomeFAQCarouselProps) => {
  const config = useConfig();
  const [show, setShow] = useState(false);
  const [webSrc, setWebSrc] = useState('');
  const theme = useTheme();
  const isScrolled = useRef(false);

  const { onPressFAQ } = useDiscoveryCardPressHandler({
    setWebSrc,
    setShow,
    openPricingSheet,
    toggleOnboardingSheet,
    onOpenServiceOfferSheet,
  });

  const FAQCards = useMemo(
    () => items.map(({ key, ...item }) => <FAQCard key={key} {...item} onPress={onPressFAQ({ key, ...item })} />),
    [items, onPressFAQ]
  );

  const Carousel = useMemo(() => <StyledRow>{FAQCards}</StyledRow>, [items, FAQCards, onPressFAQ]);

  const onScroll = useCallback(() => {
    if (!isScrolled.current) {
      isScrolled.current = true;
      Avo.discoveryCardScroll();
    }
  }, []);

  const { t } = useTranslation();

  return (
    <If
      _={!config?.hideCarousel && items.length > 0}
      _then={
        <>
          {theme.components.homeCarousel?.showTitle && <StyledTitle>{t('homePage.carousel', 'Discover')}</StyledTitle>}
          <StyledContainer onScroll={onScroll} className={className ?? 'mt-10'}>
            {Carousel}
          </StyledContainer>
          <WebView src={webSrc} show={show} onClose={() => setShow(false)} />
        </>
      }
    />
  );
};

export default FAQHomeCarousel;
